'use client';

import { useEffect, useRef } from 'react';

/**
 * Global "Editorial Noir" interactions:
 *  - custom cursor (dot + trailing ring)
 *  - 3D tilt on any [data-tilt] element
 *  - cursor "hot" state over interactive elements
 * Uses event delegation on document so it keeps working after client-side navigation.
 * Disabled on touch / coarse-pointer devices.
 */
export default function NoirEffects() {
  const dot = useRef<HTMLDivElement>(null);
  const ring = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!fine) return;

    document.body.classList.add('noir-has-cursor');

    let rx = window.innerWidth / 2, ry = window.innerHeight / 2;
    let tx = rx, ty = ry;
    let tilted: HTMLElement | null = null;
    let raf = 0;

    const resetTilt = () => {
      if (tilted) {
        tilted.style.transform = '';
        tilted.style.boxShadow = '';
        tilted = null;
      }
    };

    const onMove = (e: MouseEvent) => {
      tx = e.clientX; ty = e.clientY;
      if (dot.current) { dot.current.style.left = tx + 'px'; dot.current.style.top = ty + 'px'; }

      // hover state
      const interactive = (e.target as Element)?.closest('a, button, select, input, .tile');
      document.body.classList.toggle('noir-hot', !!interactive);

      // tilt
      const t = (e.target as Element)?.closest('[data-tilt]') as HTMLElement | null;
      if (t !== tilted) resetTilt();
      if (t) {
        tilted = t;
        const r = t.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        t.style.transform = `perspective(900px) rotateY(${px * 6}deg) rotateX(${-py * 6}deg) translateY(-4px)`;
        t.style.boxShadow = `${-px * 20}px ${py * 20 + 18}px 50px -22px rgba(0,0,0,.7)`;
      }
    };

    const onLeave = () => { document.body.classList.remove('noir-hot'); resetTilt(); };

    const loop = () => {
      rx += (tx - rx) * 0.18; ry += (ty - ry) * 0.18;
      if (ring.current) { ring.current.style.left = rx + 'px'; ring.current.style.top = ry + 'px'; }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      document.body.classList.remove('noir-has-cursor', 'noir-hot');
      resetTilt();
    };
  }, []);

  return (
    <>
      <div ref={dot} className="noir-cursor" aria-hidden />
      <div ref={ring} className="noir-cursor-ring" aria-hidden />
    </>
  );
}
